import { Box } from '@material-ui/core';
import { LenderApprovalStatus, LenderName, Loan } from '@nemuru-eng/js-api-client';
import React, { useState } from 'react';

import { useLoanApplicationHeaderActions } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/components/LoanApplicationHeader/hooks/useLoanApplicationHeaderActions';
import { LoanApplicationMode } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/types/LoanApplicationMode';
import { LenderLogo } from '@/components/shared/components/Logo/LenderLogo/LenderLogo';
import { useUserContext } from '@/contexts/UserContext/UserContext';

import { useDialog } from '../../../../../../../hooks/useDialog';
import { LoanApplicationErrorIcon } from '../../../../../../ErrorIcon/components/LoanApplicationErrorIcon';
import { Font } from '../../../../../../Font';
import { MoreActionsMenuWithIcon } from '../../../../../../MoreActionsMenu/MoreActionsMenuWithIcon';
import { ActionsOption } from '../../../../../../MoreActionsMenu/types/ActionsOption';
import { StatusTag } from '../../../../../../StatusTag/StatusTag';
import { Text } from '../../../../../../Text';
import { LoanApplicationAction } from '../../types/LoanApplicationAction';
import { getLoanApplicationStatusTagProps } from '../../utils/getTagProps';
import { LoanApplicationActionDialog } from '../LoanApplicationActionDialog/LoanApplicationActionDialog';

interface LoanApplicationHeaderProps {
  loan: Loan;
  loanApplicationId: string;
  isSelected: boolean;
  lenderName: LenderName;
  lenderApprovalStatus?: LenderApprovalStatus | null;
  mode: LoanApplicationMode;
}

export const LoanApplicationHeader = ({
  loan,
  loanApplicationId,
  lenderApprovalStatus,
  isSelected,
  lenderName,
  mode,
}: LoanApplicationHeaderProps) => {
  const { opened, setOpened, toggleDialog } = useDialog();
  const { userEntity } = useUserContext();

  const [loanApplicationAction, setLoanApplicationAction] = useState<LoanApplicationAction | undefined>();

  const handleOpenActionDialog = (action: LoanApplicationAction) => {
    setLoanApplicationAction(action);
    toggleDialog();
  };

  const isLoanApplicationSelected = loanApplicationId === loan.loanApplicationSelected?.id;

  const isLoanLenderBorrowerContractSent = isLoanApplicationSelected
    ? loan.loanApplicationSelected?.loanLenderBorrowerContract.isSent
    : undefined;

  const { getActionOptionsArray } = useLoanApplicationHeaderActions({
    loanId: loan.id,
    loanStatus: loan.status,
    lenderName: lenderName,
    isLoanLenderBorrowerContractSent,
    onClickAction: handleOpenActionDialog,
  });

  const LoanApplicationActionOptions: ActionsOption[] = getActionOptionsArray(mode);

  if (!userEntity) return null;

  return (
    <>
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        width={'100%'}
        mx={1}
        my={-1}
        className={'appear-anim'}
      >
        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <LenderLogo lenderName={lenderName} style={isSelected ? { height: 30 } : unselectedLogoStyle} />
        </Box>
        <Box display={'flex'} alignItems={'center'}>
          {lenderApprovalStatus ? (
            <Box>
              <StatusTag {...getLoanApplicationStatusTagProps(lenderApprovalStatus)} />
            </Box>
          ) : (
            <Font variant={'overline'}>
              <Box display={'flex'} alignItems={'center'}>
                {isSelected && (
                  <Box mr={3}>
                    <LoanApplicationErrorIcon />
                  </Box>
                )}
                <Text tId={'NO_RESPONSE'} />
              </Box>
            </Font>
          )}
          {mode !== LoanApplicationMode.VIEW_ONLY && (
            <Box ml={2}>
              <MoreActionsMenuWithIcon actionsOptionsArray={LoanApplicationActionOptions} />
            </Box>
          )}
        </Box>
      </Box>
      {loanApplicationAction && opened && (
        <LoanApplicationActionDialog
          loanId={loan.id}
          loanApplicationId={loanApplicationId}
          loanApplicationAction={loanApplicationAction}
          setLoanApplicationAction={setLoanApplicationAction}
          useDialogProps={{ opened, setOpened, toggleDialog }}
          lenderApprovalStatus={lenderApprovalStatus}
        />
      )}
    </>
  );
};

const unselectedLogoStyle = {
  height: 30,
  opacity: 0.5,
  filter: 'grayscale(100%) brightness(65%) contrast(150%)',
} as React.CSSProperties;
